/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 20190402 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';

import { LocationService } from '../location.service';
import { LoggingService } from '../../logging/logic/logging.service';
import { AppParameterService } from '../../app-parameter/logic/app-parameter.service';
import {
  Geolocation,
  PermissionStatus,
  WatchPositionCallback,
} from '@capacitor/geolocation';
import { LBSUtils } from 'src/app/_util/lbs-utils';
import { LocationError } from '../location-errors';
import { AppParameter } from 'src/app/_model/app-parameter';

import { registerPlugin } from '@capacitor/core';
import { BackgroundGeolocationPlugin } from '@capacitor-community/background-geolocation';
import { TranslateService } from '@ngx-translate/core';
const BackgroundGeolocation = registerPlugin<BackgroundGeolocationPlugin>(
  'BackgroundGeolocation',
);

const DEFAULT_GPS_TIMEOUT_MS = 10000;
const DEFAULT_MAX_AGE_MS = 10000;

export type BackgroundLocation = {
  // Longitude in degrees.
  longitude: number;
  // Latitude in degrees.
  latitude: number;
  // Radius of horizontal uncertainty in metres, with 68% confidence.
  accuracy: number;
  // Metres above sea level (or null).
  altitude: number;
  // Vertical uncertainty in metres, with 68% confidence (or null).
  altitudeAccuracy: number;
  // Deviation from true north in degrees (or null).
  bearing: number;
  // True if the location was simulated by software, rather than GPS.
  simulated: boolean;
  // Speed in metres per second (or null).
  speed: number;
  // Time the location was produced, in milliseconds since the unix epoch.
  time: number;
};

/**
 * Interface Definition for a Location Service
 *
 * LocalizationService provides Geolocations of the device
 */
@Injectable()
export class CapactitorLocationService implements LocationService {
  constructor(
    private logger: LoggingService,
    private appParameterService: AppParameterService,
    private translate: TranslateService,
  ) {}

  public async getCurrentLocation(): Promise<{
    longitude: number;
    latitude: number;
    accuracy: number;
    altitude: number;
    altitude_accuracy: number;
    timestamp: number;
  }> {
    const appParameters = this.appParameterService.appParameters$.getValue();
    const enableHighAccuracy = appParameters
      ? appParameters['imsmc.location.enablehighaccuracy']
        ? appParameters['imsmc.location.enablehighaccuracy']
        : false
      : false;
    let timeout = appParameters
      ? appParameters['imsmc.location.waittimeout']
        ? +appParameters['imsmc.location.waittimeout']
        : DEFAULT_GPS_TIMEOUT_MS
      : DEFAULT_GPS_TIMEOUT_MS;
    let maximumAge = appParameters
      ? appParameters['imsmc.location.maxage']
        ? +appParameters['imsmc.location.maxage']
        : DEFAULT_MAX_AGE_MS
      : DEFAULT_MAX_AGE_MS;

    this.logger.log(
      this.constructor.name,
      'Requested GPS Coordinates with high accuracy:',
      enableHighAccuracy,
      'timeout:',
      timeout,
      'maxAge:',
      maximumAge,
    );

    await this.checkRequiredPermissions(appParameters);

    try {
      // JB20260309: as of Capacitor 8 timeout should be consisntent on all plattforms - not needed anymore
      // keeping as saftynet as performance is negligible
      let coordinates = await LBSUtils.addTimeoutToPromise(
        Geolocation.getCurrentPosition({
          enableHighAccuracy: enableHighAccuracy,
          timeout: timeout,
          maximumAge: maximumAge,
        }),
        Math.floor(timeout * 1.1),
      );
      this.logger.log(
        this.constructor.name,
        'Current coordinates: ',
        coordinates,
      );
      return {
        longitude: coordinates.coords.longitude,
        latitude: coordinates.coords.latitude,
        accuracy: coordinates.coords.accuracy,
        altitude: coordinates.coords.altitude,
        altitude_accuracy: coordinates.coords.altitudeAccuracy,
        timestamp: coordinates.timestamp,
      };
    } catch (error) {
      this.logger.warn(
        this.constructor.name,
        'Run into timeout after',
        timeout,
        'milliseconds - continue without gps coordinates',
      );
      return undefined;
    }
  }

  public async startWatchingPosition(
    callBack: WatchPositionCallback,
  ): Promise<string> {
    const appParameters = this.appParameterService.appParameters$.getValue();
    await this.checkRequiredPermissions(appParameters);

    return Geolocation.watchPosition({}, callBack);
  }

  public async stopWatchingPosition(handle: string): Promise<void> {
    if (handle == null) {
      return;
    }
    return Geolocation.clearWatch({
      id: handle,
    });
  }

  public async startBackgroundWatchingPosition(
    callback: (location: BackgroundLocation, error: Error) => void,
  ): Promise<string> {
    const appParameters = this.appParameterService.appParameters$.getValue();
    await this.checkRequiredPermissions(appParameters);

    const distanceFilter = appParameters
      ? appParameters['imsmc.location.track.distance-filter']
        ? appParameters['imsmc.location.track.distance-filter']
        : 2000
      : 2000;

    return BackgroundGeolocation.addWatcher(
      {
        backgroundMessage: this.translate.instant(
          'SERVICE.LOCATION-SERVICE.BACKGROUND-WATCH-POSITION.NOTIFICATION.MESSAGE',
        ),
        backgroundTitle: this.translate.instant(
          'SERVICE.LOCATION-SERVICE.BACKGROUND-WATCH-POSITION.NOTIFICATION.TITLE',
        ),
        requestPermissions: true,
        stale: false,
        distanceFilter: distanceFilter,
      },
      callback,
    );
  }

  public async stopBackgroundWatchingPosition(handle: string): Promise<void> {
    if (handle == null) {
      return;
    }
    BackgroundGeolocation.removeWatcher({
      id: handle,
    });
  }

  private async checkRequiredPermissions(appParameters: AppParameter) {
    const enforceLocationActive =
      appParameters?.getBooleanParameter(
        'imsmc.location.enforcelocationactive',
        false,
      ) ?? false;
    const enforcedLocationMustBePrecise =
      appParameters?.getBooleanParameter(
        'imsmc.location.enforcedlocationmustbeprecise',
        false,
      ) ?? false;
    const enableHighAccuracy =
      appParameters?.getBooleanParameter(
        'imsmc.location.enablehighaccuracy',
        false,
      ) ?? false;

    this.logger.log(
      this.constructor.name,
      'Enforce GPS must be active',
      enforceLocationActive,
      '; enforced GPS must be precise',
      enforcedLocationMustBePrecise,
    );
    if (!enforceLocationActive) {
      return;
    }

    let result: PermissionStatus;
    try {
      result = await Geolocation.checkPermissions();
    } catch (e) {
      throw new Error(LocationError.LOCATION_MUST_BE_ACTIVATED);
    }

    if (enableHighAccuracy && enforcedLocationMustBePrecise) {
      await this.checkHighAccurracyPermission(result);
    } else {
      await this.checkGeneralPermission(result);
    }
  }

  private async checkHighAccurracyPermission(
    permissionStatus: PermissionStatus,
  ) {
    if (permissionStatus.location === 'granted') {
      return;
    }

    if (permissionStatus.location === 'denied') {
      throw new Error(LocationError.LOCATION_MUST_BE_ALLOWED);
    }

    const promtResult = await Geolocation.requestPermissions({
      permissions: ['location'],
    });

    await this.checkHighAccurracyPermission(promtResult);
  }

  private async checkGeneralPermission(permissionStatus: PermissionStatus) {
    if (permissionStatus.coarseLocation === 'granted') {
      return;
    }

    if (permissionStatus.coarseLocation === 'denied') {
      throw new Error(LocationError.LOCATION_MUST_BE_ALLOWED);
    }

    const promtResult = await Geolocation.requestPermissions({
      permissions: ['coarseLocation'],
    });

    await this.checkGeneralPermission(promtResult);
  }
}
